import { useState } from "react";
import { api } from "./api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useResource, useContentDoc, SectionHeader, Loading, Modal, ConfirmDialog, Field, PrimaryBtn, GhostBtn, ImagePicker, inputCls } from "./components";

const CATEGORIES = ["Game Action", "Team", "Community", "Facilities"];
const EMPTY = { caption: "", category: "Game Action", src: "" };

export default function PhotosSection() {
  const { items, loading, reload } = useResource("gallery");
  const { content, save } = useContentDoc();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [saving, setSaving] = useState(false);

  const sorted = [...items].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

  const saveKeyPhoto = async (key, url) => {
    const next = { ...content, [key]: url };
    try {
      await save(next);
      toast.success("Photo updated on the website!");
    } catch {
      toast.error("Could not save the photo.");
    }
  };

  const savePhoto = async () => {
    if (!editing.src) {
      toast.error("Please upload a photo first.");
      return;
    }
    setSaving(true);
    const payload = { caption: editing.caption.trim(), category: editing.category, src: editing.src, order: editing.order ?? items.length };
    try {
      if (editing.id) await api.put(`/admin/gallery/${editing.id}`, payload);
      else await api.post("/admin/gallery", payload);
      toast.success("Photo saved! The gallery on the website is updated.");
      setEditing(null);
      reload();
    } catch {
      toast.error("Could not save the photo.");
    }
    setSaving(false);
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/gallery/${deleting.id}`);
      toast.success("Photo deleted from the gallery.");
      setDeleting(null);
      reload();
    } catch {
      toast.error("Could not delete the photo.");
    }
  };

  const move = async (index, dir) => {
    const list = [...sorted];
    const j = index + dir;
    if (j < 0 || j >= list.length) return;
    [list[index], list[j]] = [list[j], list[index]];
    try {
      await Promise.all(list.map((p, i) => api.put(`/admin/gallery/${p.id}`, { order: i })));
      reload();
    } catch {
      toast.error("Could not reorder. Please try again.");
    }
  };

  if (loading || !content) return <Loading />;

  return (
    <div data-testid="admin-photos-section">
      <SectionHeader
        title="Photos"
        subtitle="Change the big homepage picture, the team logo, and manage the photo gallery. Videos are managed in the Videos section."
      />

      <h2 className="font-condensed text-2xl font-bold uppercase text-slate-900 mb-4">Key Website Photos</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="hero-photo-card">
          <ImagePicker label="Homepage Hero (the big photo at the top of the homepage)" value={content.hero_image} onChange={(url) => saveKeyPhoto("hero_image", url)} testid="hero-image-picker" />
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="logo-photo-card">
          <ImagePicker label="Team Logo (shown in the header and browser tab)" value={content.logo_url} onChange={(url) => saveKeyPhoto("logo_url", url)} testid="logo-image-picker" />
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-condensed text-2xl font-bold uppercase text-slate-900">Gallery Photos</h2>
        <PrimaryBtn onClick={() => setEditing({ ...EMPTY })} data-testid="add-photo-button">
          <Plus size={17} /> Add New Photo
        </PrimaryBtn>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="photos-list">
        {sorted.length === 0 && <p className="col-span-full bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500">No gallery photos yet. Click "Add New Photo".</p>}
        {sorted.map((p, i) => (
          <div key={p.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`photo-row-${p.id}`}>
            <img src={p.src} alt={p.caption} className="h-36 w-full object-cover" />
            <div className="p-3">
              <span className="bg-[#0F4C2A]/10 text-[#0F4C2A] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">{p.category}</span>
              <p className="text-xs text-slate-600 mt-1.5 truncate">{p.caption}</p>
              <div className="flex gap-2 mt-2.5">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-slate-500 hover:bg-slate-100 rounded-md disabled:opacity-30" aria-label="Move photo up" data-testid={`photo-up-${p.id}`}>
                  <ArrowUp size={13} />
                </button>
                <button onClick={() => move(i, 1)} disabled={i === sorted.length - 1} className="p-2 text-slate-500 hover:bg-slate-100 rounded-md disabled:opacity-30" aria-label="Move photo down" data-testid={`photo-down-${p.id}`}>
                  <ArrowDown size={13} />
                </button>
                <button onClick={() => setEditing({ ...p })} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#0F4C2A] bg-[#0F4C2A]/10 hover:bg-[#0F4C2A]/20 rounded-md py-2" data-testid={`photo-edit-${p.id}`}>
                  <Pencil size={13} /> Edit
                </button>
                <button onClick={() => setDeleting(p)} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wide text-red-700 bg-red-50 hover:bg-red-100 rounded-md py-2" data-testid={`photo-delete-${p.id}`}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Photo" : "Add New Photo"} onClose={() => setEditing(null)} testid="photo-modal">
          <div className="space-y-4">
            <ImagePicker label="Photo" value={editing.src} onChange={(url) => setEditing({ ...editing, src: url })} testid="photo-form-image" />
            <Field label="Caption" hint="A short description shown under the photo">
              <input className={inputCls} value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })} placeholder="e.g. Senior Night at Malmquist Field" data-testid="photo-form-caption" />
            </Field>
            <Field label="Category">
              <select className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="photo-form-category">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <div className="flex gap-3 pt-2">
              <PrimaryBtn className="flex-1" onClick={savePhoto} disabled={saving} data-testid="photo-form-save">
                {saving ? "Saving..." : "Save Photo"}
              </PrimaryBtn>
              <GhostBtn onClick={() => setEditing(null)} data-testid="photo-form-cancel">Cancel</GhostBtn>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <ConfirmDialog
          message={`Delete the photo "${deleting.caption}"? This cannot be undone.`}
          onConfirm={remove}
          onCancel={() => setDeleting(null)}
          testid="photo-delete-confirm"
        />
      )}
    </div>
  );
}
